import type { AttrTrackingMap, TrkAction } from '@unisporkal/attr-tracking';

// Parameters GA4 reports with a spelling that differs from their URL token.
// `istockexclusive` travels the URL as `yes`, but has been reported as `true`
// since the Redux era, when the parameter was held as a boolean. Kept for
// continuity with existing analytics; delete the entry to report the URL token
// instead. Both the refine panel and the applied-filter chip resolve through
// `search_filter_change`, so this is the only place the spelling is decided.
const TRACKING_VALUE_OVERRIDES: Record<string, Record<string, string>> = {
  istockexclusive: { yes: 'true' },
};

const trackingValueFor = <T>(
  paramName: string | boolean,
  value: T
): T | string =>
  TRACKING_VALUE_OVERRIDES[String(paramName)]?.[String(value)] ?? value;

export const searchAttrTrackingMap: AttrTrackingMap = {
  mfeId: 'search',
  events: {
    toggle_refine_panel: (action: TrkAction) => {
      const eventName = action.context.isOpen
        ? 'close_refine_panel'
        : 'open_refine_panel';

      return {
        event: 'search_interaction',
        event_name: eventName,
        search_interaction: eventName,
        search_selection: undefined,
        ui_element_location: 'refine_panel',
        search_grid_slot_number: undefined,
      };
    },
    language_selection: () => ({
      event: 'search_interaction',
      event_name: 'language_selection',
      search_interaction: 'language_selection',
      search_selection: 'country_or_region',
      ui_element_location: undefined,
      search_grid_slot_number: undefined,
    }),
    toggle_search_by_asset_results: (action: TrkAction) => {
      const selectedAssetType = action.context.selectedAssetType as string;
      const trackingValue =
        selectedAssetType === 'film' ? 'video' : selectedAssetType;

      return {
        event: 'search_interaction',
        event_name: 'toggle_search_by_asset_results',
        search_interaction: 'toggle_search_by_asset_results',
        search_selection: `toggle_to_${trackingValue}`,
        ui_element_location: 'search_by_asset_toggle',
        search_grid_slot_number: undefined,
      };
    },
    toggle_to_opposite_asset_type: (action: TrkAction) => {
      const oppositeAssetType = action.context.oppositeAssetType as string;
      const { isCreativeOnly } = action.context;
      const eventName =
        oppositeAssetType === 'film'
          ? 'toggle_to_video_results'
          : 'toggle_to_image_results';
      const locationSuffix = oppositeAssetType === 'film' ? 'videos' : 'images';
      const locationPrefix = isCreativeOnly ? 'creative' : 'mixed';

      return {
        event: 'search_interaction',
        event_name: eventName,
        search_interaction: eventName,
        search_selection: undefined,
        ui_element_location: `asset_type_pivot_link_${locationPrefix}_${locationSuffix}`,
        search_grid_slot_number: undefined,
      };
    },
    search_similar_asset: (action: TrkAction) => {
      const TRACKING_KEYS: Record<string, string> = {
        image: 'similar_images',
        film: 'similar_videos',
        videotemplate: 'similar_videotemplates',
      };
      const assetType = action.context.assetType as string;
      const slot = Number(action.context.slot);

      return {
        event: 'search_interaction',
        event_name: `search_${TRACKING_KEYS[assetType] ?? assetType}`,
        search_interaction: `search_${TRACKING_KEYS[assetType] ?? assetType}`,
        search_selection: 'more_like_this',
        ui_element_location: 'asset_overlay_cta',
        search_grid_slot_number: slot,
      };
    },
    modal_shown: (action: TrkAction) => ({
      event: 'modal_interaction',
      event_name: 'modal_shown',
      modal_interaction: 'modal_shown',
      modal_content: action.context.modal_content,
      modal_type: action.context.modal_type,
      ui_element_location: 'modal',
      item_id: action.context.item_id,
    }),
    modal_cta_click: (action: TrkAction) => ({
      event: 'modal_interaction',
      event_name: 'modal_cta_click',
      modal_interaction: 'modal_cta_click',
      modal_content: action.context.modal_content,
      modal_type: action.context.modal_type,
      cta_text: action.context.cta_text,
      ui_element_location: action.context.ui_element_location ?? 'modal_cta',
      modal_selection: action.context.modal_selection,
      item_id: action.context.item_id,
    }),
    close_modal: (action: TrkAction) => ({
      event: 'modal_interaction',
      event_name: 'close_modal',
      modal_interaction: 'close_modal',
      modal_content: action.context.modal_content,
      modal_type: action.context.modal_type,
      ui_element_location: 'modal_x',
      item_id: action.context.item_id,
      modal_selection: 'close',
    }),
    // Legacy payload omitted modal_content/modal_type; preserve the empty strings.
    cta_clicked_product_background: () => ({
      event: 'modal_interaction',
      event_name: 'cta_clicked',
      modal_interaction: 'cta_clicked',
      modal_content: '',
      modal_type: '',
      cta_text: 'use_for_product_background',
      ui_element_location: 'product_background_button',
    }),
    // AI-free hero on the top-level editorial landing page. Static payload:
    // this hero is the only one in the search app, so it renders exactly one
    // link and `cta_text` is fixed. Its Getty creative counterpart lives in
    // the landing app and reports its own label.
    ai_free_hero_cta: () => ({
      event: 'cta_interaction',
      event_name: 'cta_clicked',
      cta_interaction: 'cta_clicked',
      cta_selection: undefined,
      cta_text: 'learn_about_our_editorial_policies',
      ui_element_location: 'hp_hero_banner',
    }),
    carousel_scroll_left: () => ({
      event: 'carousel_interaction',
      event_name: 'carousel_scroll_left',
      carousel_interaction: 'carousel_scroll_left',
      carousel_selection: undefined,
      ui_element_location: 'sticky_container',
    }),
    carousel_scroll_right: () => ({
      event: 'carousel_interaction',
      event_name: 'carousel_scroll_right',
      carousel_interaction: 'carousel_scroll_right',
      carousel_selection: undefined,
      ui_element_location: 'sticky_container',
    }),
    navigation: (action: TrkAction) => ({
      event: 'navigation',
      event_model: 'navigation',
      event_name: 'navigation',
      link_text: action.context.linkText,
      link_url: action.context.linkUrl,
      navigation_selection: action.context.navigationSelection,
      ui_element_location: action.containerId,
    }),
    view_banner: (action: TrkAction) => ({
      event: 'banner_interaction',
      event_name: 'view_banner',
      banner_interaction: 'view_banner',
      banner_name: action.context.bannerName,
      banner_type: 'promotion',
      ui_element_location: action.context.uiLocation ?? action.containerId,
    }),
    banner_cta_click: (action: TrkAction) => ({
      event: 'banner_interaction',
      event_name: 'banner_cta_click',
      banner_interaction: 'banner_cta_click',
      banner_name: action.context.bannerName,
      banner_type: 'promotion',
      ui_element_location: action.context.uiLocation ?? action.containerId,
      cta_text: action.context.ctaText,
      ...(action.context.linkText && { link_text: action.context.linkText }),
    }),
    close_banner: (action: TrkAction) => ({
      event: 'banner_interaction',
      event_name: 'close_banner',
      banner_interaction: 'close_banner',
      banner_name: action.context.bannerName,
      banner_type: 'promotion',
      ui_element_location: action.context.uiLocation ?? action.containerId,
      cta_text: action.context.ctaText,
    }),
    notification_cta_click: (action: TrkAction) => ({
      event: 'notification_interaction',
      event_name: 'notification_cta_click',
      notification_interaction: 'notification_cta_click',
      notification_type: 'promotion',
      notification_description: action.context.notificationDescription,
      cta_text: action.context.ctaText,
      cta_url: action.context.ctaUrl,
      item_id: action.context.itemId,
      ui_element_location: action.context.uiLocation,
    }),
    // Preview link tracking spec (ClickUp 86bb676xe) — banner viewed/dismissed on the SRP.
    notification_shown: (action: TrkAction) => ({
      event: 'notification_interaction',
      event_name: 'notification_shown',
      notification_interaction: 'notification_shown',
      notification_description: action.context.notificationDescription,
      notification_type: undefined,
      cta_text: undefined,
      cta_url: undefined,
      ui_element_location: 'srp_preview_link_banner',
      item_id: undefined,
      event_model: 'notification_interaction',
    }),
    dismiss_notification: (action: TrkAction) => ({
      event: 'notification_interaction',
      event_name: 'dismiss_notification',
      notification_interaction: 'dismiss_notification',
      notification_description: action.context.notificationDescription,
      notification_type: undefined,
      cta_text: 'exit_preview',
      cta_url: undefined,
      ui_element_location: 'srp_preview_link_banner',
      item_id: undefined,
      event_model: 'notification_interaction',
    }),
    search_filter_change: (action: TrkAction) => {
      const { paramName, selected, uiLocation } = action.context;
      // For clear events (selected=true), context.value encodes the previous filter value being
      // cleared (e.g. prevMinValue on a range slider). targetValue would be the reset position
      // (e.g. 0) which is incorrect here.
      // For regular refine events, prefer targetValue (live DOM) to avoid stale renders.
      const rawValue = selected
        ? action.context.value ?? ''
        : action.targetValue ?? action.context.value ?? '';
      const value = trackingValueFor(paramName, rawValue);

      const eventName = selected ? 'clear_search_filter' : 'search_refine';
      const searchSelection = `${paramName}: ${value}`;

      return {
        event: 'search_refine_interaction',
        event_name: eventName,
        search_interaction: eventName,
        search_selection: searchSelection,
        search_filter: searchSelection,
        ui_element_location: uiLocation ?? action.containerId,
      };
    },
    clear_all_filters: (action: TrkAction) => {
      const { uiLocation } = action.context;

      return {
        event: 'search_refine_interaction',
        event_name: 'clear_search_filter',
        search_interaction: 'clear_search_filter',
        search_selection: 'all',
        search_filter: 'all',
        ui_element_location: uiLocation ?? action.containerId,
      };
    },
    search_display_change: (action: TrkAction) => {
      const { type, uiLocation, pageDepth, lastPage } = action.context;
      const typed = Number(action.targetValue);
      const clamped =
        typed >= 1 ? Math.min(typed, Number(lastPage)) : undefined;
      const depth = pageDepth ? Number(pageDepth) : clamped;

      return {
        event: 'search_refine_interaction',
        event_name: 'search_results_display_change',
        search_interaction: 'search_results_display_change',
        search_selection: type,
        search_filter: type,
        ui_element_location: uiLocation,
        ...(depth !== undefined && { search_results_page_depth: depth }),
      };
    },
  },
};
